import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import {
  Calendar, ArrowLeft, RefreshCw, Plus, Play, Pause, Trash2,
  Clock, FileText, Mail, Settings, BarChart3, CheckCircle,
  XCircle, Loader2, Edit, Eye, Send, AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FREQUENCY_LABELS = {
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Bi-weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly'
};

const REPORT_TYPE_LABELS = {
  tmf_completeness: 'TMF Completeness',
  compliance_summary: 'Compliance Summary',
  document_activity: 'Document Activity',
  audit_trail: 'Audit Trail',
  study_health: 'Study Health'
};

const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function ScheduledReportsPage() {
  const { getAuthHeaders, selectedStudy: contextStudy } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [stats, setStats] = useState(null);
  const [studies, setStudies] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('schedules');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  
  // Form state - Use context study as default
  const [formData, setFormData] = useState({
    name: '',
    report_type: 'tmf_completeness',
    frequency: 'weekly',
    delivery_hour: 8,
    delivery_day: 1,
    recipients: [],
    parameters: { study_id: contextStudy?.study_id || undefined },
    include_csv: true,
    enabled: true
  });

  // Set selected study from context when creating new schedule
  useEffect(() => {
    if (contextStudy?.study_id) {
      setFormData(prev => ({ 
        ...prev, 
        parameters: { ...prev.parameters, study_id: contextStudy.study_id }
      }));
    }
  }, [contextStudy]);
  const [recipientInput, setRecipientInput] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { headers } = getAuthHeaders();
      
      const [schedulesRes, statsRes, studiesRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/scheduled-reports`, { headers }),
        axios.get(`${API_URL}/api/scheduled-reports/stats/overview`, { headers }),
        axios.get(`${API_URL}/api/studies`, { headers }),
        axios.get(`${API_URL}/api/users`, { headers }).catch(() => ({ data: { users: [] } }))
      ]);
      
      setSchedules(schedulesRes.data?.schedules || []);
      setStats(statsRes.data);
      setStudies(studiesRes.data?.studies || []);
      setUsers(usersRes.data?.users || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      name: '',
      report_type: 'tmf_completeness',
      frequency: 'weekly',
      delivery_hour: 8,
      delivery_day: 1,
      recipients: [],
      parameters: {},
      include_csv: true,
      enabled: true
    });
    setRecipientInput('');
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    setActionLoading(true);
    try {
      const { headers } = getAuthHeaders();
      await axios.post(`${API_URL}/api/scheduled-reports`, formData, { headers });
      toast.success('Scheduled report created');
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create schedule');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedSchedule) return;
    
    setActionLoading(true);
    try {
      const { headers } = getAuthHeaders();
      await axios.put(`${API_URL}/api/scheduled-reports/${selectedSchedule.id}`, formData, { headers });
      toast.success('Schedule updated');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update schedule');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSchedule) return;
    
    setActionLoading(true);
    try {
      const { headers } = getAuthHeaders();
      await axios.delete(`${API_URL}/api/scheduled-reports/${selectedSchedule.id}`, { headers });
      toast.success('Schedule deleted');
      setShowDeleteDialog(false);
      setSelectedSchedule(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete schedule');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggle = async (schedule, enabled) => {
    try {
      const { headers } = getAuthHeaders();
      await axios.post(`${API_URL}/api/scheduled-reports/${schedule.id}/toggle?enabled=${enabled}`, {}, { headers });
      toast.success(enabled ? 'Schedule enabled' : 'Schedule disabled');
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle schedule');
    }
  };

  const handleRunNow = async (schedule) => {
    try {
      const { headers } = getAuthHeaders();
      toast.loading('Running report...', { id: 'run-report' });
      const result = await axios.post(`${API_URL}/api/scheduled-reports/${schedule.id}/run-now`, {}, { headers });
      toast.dismiss('run-report');
      
      if (result.data?.successful > 0) {
        toast.success(`Report executed successfully`);
      } else if (result.data?.failed > 0) {
        toast.error(`Report execution failed`);
      } else {
        toast.info('Report queued for execution');
      }
      fetchData();
    } catch (error) {
      toast.dismiss('run-report');
      toast.error('Failed to run report');
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    setPreviewData(null);
    
    try {
      const { headers } = getAuthHeaders();
      
      // Map report type to endpoint
      const reportEndpoints = {
        tmf_completeness: '/api/reports/tmf-completeness',
        compliance_summary: '/api/reports/compliance-summary',
        document_activity: '/api/reports/document-activity',
        audit_trail: '/api/reports/audit-trail',
        study_health: '/api/reports/study-health'
      };
      
      const endpoint = reportEndpoints[formData.report_type];
      if (!endpoint) {
        toast.error('Invalid report type');
        return;
      }
      
      // Build params
      const params = { ...formData.parameters };
      if (formData.report_type === 'study_health' && !params.study_id) {
        toast.error('Study Health Report requires a study selection');
        return;
      }
      
      const response = await axios.post(`${API_URL}${endpoint}`, params, { headers });
      setPreviewData(response.data);
      setShowPreviewDialog(true);
      toast.success('Preview generated');
      
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const openEditDialog = (schedule) => {
    setSelectedSchedule(schedule);
    setFormData({
      name: schedule.name || '',
      report_type: schedule.report_type || 'tmf_completeness',
      frequency: schedule.frequency || 'weekly',
      delivery_hour: schedule.delivery_hour || 8,
      delivery_day: schedule.delivery_day || 1,
      recipients: schedule.recipients || [],
      parameters: schedule.parameters || {},
      include_csv: schedule.include_csv !== false,
      enabled: schedule.enabled !== false
    });
    setShowEditDialog(true);
  };

  const addRecipient = () => {
    const email = recipientInput.trim().toLowerCase();
    if (email && !formData.recipients.includes(email)) {
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setFormData(prev => ({ ...prev, recipients: [...prev.recipients, email] }));
        setRecipientInput('');
      } else {
        toast.error('Invalid email address');
      }
    }
  };

  const removeRecipient = (email) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients.filter(r => r !== email)
    }));
  };

  const getStatusBadge = (schedule) => {
    if (!schedule.enabled) {
      return <Badge className="bg-slate-500">Disabled</Badge>;
    }
    if (schedule.last_status === 'failed') {
      return <Badge className="bg-red-500">Failed</Badge>;
    }
    if (schedule.last_status === 'success') {
      return <Badge className="bg-green-500">Active</Badge>;
    }
    return <Badge className="bg-blue-500">Pending</Badge>;
  };

  const formatNextRun = (nextRun) => {
    if (!nextRun) return 'Not scheduled';
    try {
      const date = parseISO(nextRun);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-slate-700/50 bg-gradient-to-r from-slate-900 to-slate-800 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard?view=all')}
                className="text-white/70 hover:text-white hover:bg-slate-50/10"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-slate-50/20"></div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Scheduled Reports</h1>
                  <p className="text-xs text-white/60">Automated Report Generation</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchData}
                className="border-white/20 text-white hover:bg-slate-50/10"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button
                size="sm"
                onClick={() => { resetForm(); setShowCreateDialog(true); }}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="create-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Schedule
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-slate-100/80 border border-slate-300 mb-6">
              <TabsTrigger value="schedules" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white" data-testid="tab-schedules">
                <Calendar className="w-4 h-4 mr-2" />
                Schedules ({schedules.length})
              </TabsTrigger>
              <TabsTrigger value="overview" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white" data-testid="tab-overview">
                <BarChart3 className="w-4 h-4 mr-2" />
                Overview
              </TabsTrigger>
            </TabsList>

            {/* Schedules Tab */}
            <TabsContent value="schedules" data-testid="schedules-content">
              {schedules.length === 0 ? (
                <Card className="bg-slate-50/90 border-slate-300 backdrop-blur-sm">
                  <CardContent className="p-12 text-center">
                    <Calendar className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">No Scheduled Reports</h3>
                    <p className="text-slate-600 mb-6">Create automated report schedules to receive reports via email</p>
                    <Button onClick={() => { resetForm(); setShowCreateDialog(true); }} className="bg-teal-600 hover:bg-teal-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Create First Schedule
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {schedules.map((schedule) => (
                    <Card key={schedule.id} className="bg-slate-50/90 border-slate-300 backdrop-blur-sm hover:border-teal-300 transition-all" data-testid={`schedule-card-${schedule.id}`}>
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-slate-900">{schedule.name}</h3>
                              {getStatusBadge(schedule)}
                              <Badge variant="outline">{FREQUENCY_LABELS[schedule.frequency] || schedule.frequency}</Badge>
                            </div>
                            
                            <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                              <span className="flex items-center gap-1">
                                <FileText className="w-4 h-4" />
                                {REPORT_TYPE_LABELS[schedule.report_type] || schedule.report_type}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {schedule.delivery_hour}:00 UTC
                                {schedule.frequency === 'weekly' && ` on ${DAY_LABELS[schedule.delivery_day]}`}
                                {schedule.frequency === 'monthly' && ` on day ${schedule.delivery_day}`}
                              </span>
                              <span className="flex items-center gap-1">
                                <Mail className="w-4 h-4" />
                                {schedule.recipients?.length || 0} recipients
                              </span>
                            </div>
                            
                            <div className="flex gap-4 mt-2 text-xs text-slate-500">
                              <span>Next run: {formatNextRun(schedule.next_run)}</span>
                              {schedule.last_run && (
                                <span>Last run: {format(parseISO(schedule.last_run), 'MMM dd, HH:mm')}</span>
                              )}
                              <span>Runs: {schedule.run_count || 0}</span>
                            </div>
                            
                            {schedule.last_error && (
                              <div className="mt-2 text-xs text-red-600 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                {schedule.last_error}
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2 ml-4">
                            <Switch
                              checked={schedule.enabled}
                              onCheckedChange={(checked) => handleToggle(schedule, checked)}
                              data-testid={`toggle-${schedule.id}`}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRunNow(schedule)}
                              disabled={!schedule.enabled}
                              className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                              data-testid={`run-${schedule.id}`}
                            >
                              <Play className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(schedule)}
                              className="text-slate-600 hover:text-slate-800"
                              data-testid={`edit-${schedule.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedSchedule(schedule); setShowDeleteDialog(true); }}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-${schedule.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Overview Tab */}
            <TabsContent value="overview" data-testid="overview-content">
              <div className="grid grid-cols-4 gap-4 mb-6">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-blue-600">{stats?.total || 0}</p>
                    <p className="text-sm text-blue-700">Total Schedules</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-green-600">{stats?.enabled || 0}</p>
                    <p className="text-sm text-green-700">Active</p>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-amber-600">{stats?.disabled || 0}</p>
                    <p className="text-sm text-amber-700">Disabled</p>
                  </CardContent>
                </Card>
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-purple-600">{stats?.upcoming?.length || 0}</p>
                    <p className="text-sm text-purple-700">Upcoming</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {/* By Frequency */}
                <Card className="bg-slate-50/90 border-slate-300">
                  <CardHeader>
                    <CardTitle className="text-slate-900 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-teal-600" />
                      By Frequency
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {Object.entries(stats?.by_frequency || {}).length > 0 ? (
                      <div className="space-y-2">
                        {Object.entries(stats.by_frequency).map(([freq, count]) => (
                          <div key={freq} className="flex justify-between items-center p-2 bg-white rounded border">
                            <span className="text-slate-700">{FREQUENCY_LABELS[freq] || freq}</span>
                            <Badge>{count}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-4">No data</p>
                    )}
                  </CardContent>
                </Card>

                {/* By Report Type */}
                <Card className="bg-slate-50/90 border-slate-300">
                  <CardHeader>
                    <CardTitle className="text-slate-900 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-teal-600" />
                      By Report Type
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {Object.entries(stats?.by_type || {}).length > 0 ? (
                      <div className="space-y-2">
                        {Object.entries(stats.by_type).map(([type, count]) => (
                          <div key={type} className="flex justify-between items-center p-2 bg-white rounded border">
                            <span className="text-slate-700">{REPORT_TYPE_LABELS[type] || type}</span>
                            <Badge>{count}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-4">No data</p>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Runs */}
                <Card className="bg-slate-50/90 border-slate-300">
                  <CardHeader>
                    <CardTitle className="text-slate-900 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-teal-600" />
                      Recent Executions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {stats?.recent_runs?.length > 0 ? (
                      <div className="space-y-2">
                        {stats.recent_runs.map((run) => (
                          <div key={run.id} className="flex justify-between items-center p-2 bg-white rounded border">
                            <div>
                              <p className="text-sm font-medium text-slate-900">{run.name}</p>
                              <p className="text-xs text-slate-500">
                                {run.last_run ? format(parseISO(run.last_run), 'MMM dd, HH:mm') : 'Never'}
                              </p>
                            </div>
                            <Badge className={run.last_status === 'success' ? 'bg-green-500' : 'bg-red-500'}>
                              {run.last_status || 'pending'}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-4">No recent executions</p>
                    )}
                  </CardContent>
                </Card>

                {/* Upcoming */}
                <Card className="bg-slate-50/90 border-slate-300">
                  <CardHeader>
                    <CardTitle className="text-slate-900 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-teal-600" />
                      Upcoming Schedules
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {stats?.upcoming?.length > 0 ? (
                      <div className="space-y-2">
                        {stats.upcoming.map((item) => (
                          <div key={item.id} className="flex justify-between items-center p-2 bg-white rounded border">
                            <div>
                              <p className="text-sm font-medium text-slate-900">{item.name}</p>
                              <p className="text-xs text-slate-500">{FREQUENCY_LABELS[item.frequency]}</p>
                            </div>
                            <span className="text-xs text-teal-600 font-medium">
                              {formatNextRun(item.next_run)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-4">No upcoming schedules</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog || showEditDialog} onOpenChange={(open) => { 
        if (!open) { setShowCreateDialog(false); setShowEditDialog(false); }
      }}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>{showEditDialog ? 'Edit Schedule' : 'Create Scheduled Report'}</DialogTitle>
            <DialogDescription>
              {showEditDialog ? 'Update the schedule configuration' : 'Configure automatic report generation and email delivery'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Schedule Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., Weekly TMF Report"
                  data-testid="input-name"
                />
              </div>
              
              <div>
                <Label>Report Type</Label>
                <Select 
                  value={formData.report_type} 
                  onValueChange={(v) => setFormData(p => ({ ...p, report_type: v }))}
                >
                  <SelectTrigger data-testid="select-report-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(REPORT_TYPE_LABELS).map(([id, label]) => (
                      <SelectItem key={id} value={id}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Frequency</Label>
                <Select 
                  value={formData.frequency} 
                  onValueChange={(v) => setFormData(p => ({ ...p, frequency: v }))}
                >
                  <SelectTrigger data-testid="select-frequency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(FREQUENCY_LABELS).map(([id, label]) => (
                      <SelectItem key={id} value={id}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Delivery Hour (UTC)</Label>
                <Select 
                  value={String(formData.delivery_hour)} 
                  onValueChange={(v) => setFormData(p => ({ ...p, delivery_hour: parseInt(v) }))}
                >
                  <SelectTrigger data-testid="select-hour">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => (
                      <SelectItem key={i} value={String(i)}>{i.toString().padStart(2, '0')}:00</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {formData.frequency === 'weekly' && (
                <div>
                  <Label>Day of Week</Label>
                  <Select 
                    value={String(formData.delivery_day)} 
                    onValueChange={(v) => setFormData(p => ({ ...p, delivery_day: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="select-day">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAY_LABELS.map((day, idx) => (
                        <SelectItem key={idx} value={String(idx)}>{day}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {(formData.frequency === 'monthly' || formData.frequency === 'quarterly') && (
                <div>
                  <Label>Day of Month</Label>
                  <Select 
                    value={String(formData.delivery_day)} 
                    onValueChange={(v) => setFormData(p => ({ ...p, delivery_day: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="select-day">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 28 }, (_, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            {/* Parameters based on report type */}
            {(formData.report_type === 'tmf_completeness' || formData.report_type === 'compliance_summary' || formData.report_type === 'document_activity' || formData.report_type === 'study_health') && (
              <div>
                <Label>Filter by Study (optional)</Label>
                <Select 
                  value={formData.parameters?.study_id || 'all'} 
                  onValueChange={(v) => setFormData(p => ({ 
                    ...p, 
                    parameters: { ...p.parameters, study_id: v === 'all' ? undefined : v }
                  }))}
                >
                  <SelectTrigger data-testid="select-study">
                    <SelectValue placeholder="All Studies" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Studies</SelectItem>
                    {studies.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Recipients */}
            <div>
              <Label>Email Recipients</Label>
              <div className="flex gap-2">
                <Input
                  value={recipientInput}
                  onChange={(e) => setRecipientInput(e.target.value)}
                  placeholder="Enter email address"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRecipient())}
                  data-testid="input-recipient"
                />
                <Button type="button" variant="outline" onClick={addRecipient}>Add</Button>
              </div>
              {formData.recipients.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.recipients.map((email) => (
                    <Badge key={email} variant="secondary" className="cursor-pointer" onClick={() => removeRecipient(email)}>
                      {email} <XCircle className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-500 mt-1">If no recipients, report will be sent to your email</p>
            </div>
            
            {/* Options */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.include_csv}
                  onCheckedChange={(checked) => setFormData(p => ({ ...p, include_csv: checked }))}
                  data-testid="switch-csv"
                />
                <Label>Include CSV attachment</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.enabled}
                  onCheckedChange={(checked) => setFormData(p => ({ ...p, enabled: checked }))}
                  data-testid="switch-enabled"
                />
                <Label>Enabled</Label>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex justify-between sm:justify-between">
            <Button 
              variant="outline" 
              onClick={handlePreview} 
              disabled={previewLoading}
              className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
              data-testid="preview-btn"
            >
              {previewLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              Preview Report
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setShowCreateDialog(false); setShowEditDialog(false); }}>
                Cancel
              </Button>
              <Button 
                onClick={showEditDialog ? handleUpdate : handleCreate} 
                disabled={actionLoading}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="submit-btn"
              >
                {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {showEditDialog ? 'Update' : 'Create'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Delete Schedule</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{selectedSchedule?.name}&quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete} 
              disabled={actionLoading}
              data-testid="confirm-delete-btn"
            >
              {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="bg-white max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-indigo-600" />
              Report Preview: {REPORT_TYPE_LABELS[formData.report_type]}
            </DialogTitle>
            <DialogDescription>
              This is a preview of the report that will be generated. Data is based on current parameters.
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="flex-1 max-h-[60vh]">
            {previewData ? (
              <div className="space-y-4 p-4">
                {/* Summary Cards */}
                {previewData.summary && (
                  <div className="grid grid-cols-4 gap-3">
                    {Object.entries(previewData.summary).map(([key, value]) => (
                      <Card key={key} className="bg-slate-50 border-slate-200">
                        <CardContent className="p-3 text-center">
                          <p className="text-2xl font-bold text-slate-800">
                            {typeof value === 'number' ? (key.includes('rate') || key.includes('completeness') ? `${value}%` : value) : value}
                          </p>
                          <p className="text-xs text-slate-600 capitalize">{key.replace(/_/g, ' ')}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
                
                {/* Report Data */}
                {previewData.studies && previewData.studies.length > 0 && (
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm">Studies ({previewData.studies.length})</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="p-2 text-left">Study</th>
                            <th className="p-2 text-center">Completeness</th>
                            <th className="p-2 text-center">Readiness</th>
                            <th className="p-2 text-center">Alerts</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.studies.slice(0, 10).map((study, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="p-2">{study.study_name || study.name}</td>
                              <td className="p-2 text-center">
                                <Badge className={study.overall_completeness >= 80 ? 'bg-green-500' : study.overall_completeness >= 50 ? 'bg-amber-500' : 'bg-red-500'}>
                                  {study.overall_completeness}%
                                </Badge>
                              </td>
                              <td className="p-2 text-center">
                                <Badge variant="outline">{study.inspection_readiness}</Badge>
                              </td>
                              <td className="p-2 text-center">{study.alerts?.critical || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {previewData.studies.length > 10 && (
                        <p className="text-xs text-slate-500 p-2 text-center">
                          Showing 10 of {previewData.studies.length} studies
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}
                
                {/* Compliance by Regulation */}
                {previewData.by_regulation && previewData.by_regulation.length > 0 && (
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm">By Regulation</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {previewData.by_regulation.map((reg, idx) => (
                        <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                          <span className="text-sm">{reg.regulation_name}</span>
                          <div className="flex gap-2 items-center">
                            <span className="text-xs text-slate-500">{reg.total_assessments} assessments</span>
                            <Badge className={reg.average_score >= 80 ? 'bg-green-500' : 'bg-amber-500'}>
                              {reg.average_score}%
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
                
                {/* Activity Data */}
                {previewData.by_action && Object.keys(previewData.by_action).length > 0 && (
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm">Activity by Action</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {Object.entries(previewData.by_action).slice(0, 8).map(([action, count], idx) => (
                        <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                          <span className="text-sm capitalize">{action.replace(/_/g, ' ')}</span>
                          <Badge>{count}</Badge>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
                
                {/* Generated At */}
                <p className="text-xs text-slate-500 text-center">
                  Preview generated at: {previewData.generated_at ? format(parseISO(previewData.generated_at), 'MMM dd, yyyy HH:mm') : 'N/A'}
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-center py-12">
                <p className="text-slate-500">No preview data available</p>
              </div>
            )}
          </ScrollArea>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
